import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  MessageCardView,
} from "@/components/play/MessageCardView";
import {
  MessageViewModeProvider,
  type MessageViewMode,
  MESSAGE_VIEW_MODES,
} from "@/lib/message-view-mode";
import { cn } from "@/lib/utils";
import {
  FlaskConical, ArrowLeft, Filter, Swords,
  MessageSquare, HelpCircle, MapPin, Speech, Cog, Sparkles,
} from "lucide-react";
import {
  SAMPLE_MESSAGES,
  SAMPLE_ENTITIES,
  SAMPLE_ADVENTURE_STATE,
} from "@/test/sample-conversation";
import GameplayHeader from "@/components/play/GameplayHeader";
import { PlayAdminBar } from "@/components/play/PlayAdminBar";
import { QuickActionBubble } from "@/components/play/QuickActionBubble";
import { MessageListWithHiding, classifyMessage } from "@/components/play/PlayMessageList";
import { V2HUDTrigger } from "@/components/play/V2HUDTrigger";
import { V2HUDPanel } from "@/components/play/V2HUDPanel";

const MESSAGE_TABS = [
  { key: "all", label: "All", icon: MessageSquare },
  { key: "story", label: "Story", icon: Sparkles },
  { key: "action", label: "Action", icon: Swords },
  { key: "dialogue", label: "Dialogue", icon: Speech },
  { key: "exploration", label: "Explore", icon: MapPin },
  { key: "system", label: "System", icon: Cog },
  { key: "ooc", label: "OOC", icon: HelpCircle },
] as const;

const noop = () => {};

/**
 * Dev-only page that renders a sample conversation through the full
 * MessageCardView pipeline, mirroring the real Play page UI.
 * Exercises chat + prose modes, multi-intent tags, compound actions,
 * dice rolls, director notes, sub-agent avatars, validity checks,
 * OOC/system/GM messages, and more.
 *
 * Route: /dev/message-test (no auth required)
 */
export default function MessageTestPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<MessageViewMode>("chat");
  const [chatFilter, setChatFilter] = useState<string>("all");
  const [hudPanelOpen, setHudPanelOpen] = useState(false);

  // ── Message filtering (mirrors Play.tsx) ──
  const getMessageCategory = useCallback((msg: any): string => classifyMessage(msg), []);

  const filteredMessages = useMemo(() => {
    if (chatFilter === "all") return SAMPLE_MESSAGES;
    return SAMPLE_MESSAGES.filter((msg) => getMessageCategory(msg) === chatFilter);
  }, [chatFilter, getMessageCategory]);

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const msg of SAMPLE_MESSAGES) {
      const cat = getMessageCategory(msg);
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [getMessageCategory]);

  // Flow context: determine adjacent narrator messages
  const messagesWithFlow = useMemo(() => {
    return filteredMessages.map((msg, i) => {
      const isNarrator = msg.role === "narrator";
      const prev = i > 0 ? filteredMessages[i - 1] : null;
      const next = i < filteredMessages.length - 1 ? filteredMessages[i + 1] : null;
      return {
        msg,
        isPrevNarrator: !!(prev && prev.role === "narrator" && isNarrator),
        isNextNarrator: !!(next && next.role === "narrator" && isNarrator),
      };
    });
  }, [filteredMessages]);

  const firstNarratorMsgId = useMemo(() => {
    const first = SAMPLE_MESSAGES.find((m: any) => m.role === "narrator" && (m.turn === 0 || m.turn === 1));
    return first?.id ?? null;
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ── Header (mirrors Play.tsx V2) ── */}
      <GameplayHeader worldId="test-world" adventureId="test-adventure" isWorldOwner={true} isAdmin={true} />

      {/* ── Main layout: conversation area + HUD (mirrors Play.tsx) ── */}
      <div className="h-[calc(100vh-3.5rem)] flex bg-background overflow-hidden max-w-[100vw]">
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* ── Admin bar ── */}
          <PlayAdminBar
            isWorldOwner={true}
            worldId="test-world"
            adventureId="test-adventure"
            adventureName="Sample Adventure — Message Test Lab"
            isMultiplayer={false}
            turnMode="free_form"
            currentTurnPlayer={null}
            isMyTurn={true}
            otherUsers={[]}
            designIssuesCount={0}
          />

          {/* ── Message list ── */}
          <ScrollArea className="flex-1 px-2 py-2 sm:p-4 min-w-0 w-full overflow-hidden [&_[data-radix-scroll-area-viewport]]:!overflow-x-hidden [&_[data-radix-scroll-area-viewport]]:!max-w-full [&_[data-radix-scroll-area-scrollbar]]:w-1.5">
            <MessageViewModeProvider mode={viewMode}>
              <div className={cn(
                "mx-auto space-y-3 sm:space-y-4 overflow-hidden w-full min-w-0 [overflow-wrap:anywhere]",
                viewMode === "prose"
                  ? "max-w-4xl space-y-0 sm:space-y-0"
                  : "max-w-3xl space-y-3 sm:space-y-4",
              )}>
                <MessageListWithHiding
                  messages={filteredMessages}
                  hiddenIds={new Set()}
                  onToggleHide={noop}
                  renderMessage={(msg) => {
                    const msgIndex = filteredMessages.findIndex(m => m.id === msg.id);
                    const isNarratorMsg = msg.role === "narrator";
                    const msgCat = classifyMessage(msg);
                    const isStoryNarrator = isNarratorMsg && msgCat !== "system" && msgCat !== "ooc" && msgCat !== "gm_query" && msgCat !== "introspection";
                    const lastStoryNarratorIdx = filteredMessages.map((m, i) => {
                      const r = m.role === "narrator";
                      const c = classifyMessage(m);
                      return r && c !== "system" && c !== "ooc" && c !== "gm_query" && c !== "introspection" ? i : -1;
                    }).filter(i => i >= 0).slice(-1)[0] ?? -1;

                    const canRetryLast = isStoryNarrator && msgIndex === lastStoryNarratorIdx;

                    // Flow context for prose view mode
                    const prevMsg = msgIndex > 0 ? filteredMessages[msgIndex - 1] : null;
                    const nextMsg = msgIndex < filteredMessages.length - 1 ? filteredMessages[msgIndex + 1] : null;
                    const isPrevNarrator = prevMsg ? (prevMsg.role === "narrator") && isNarratorMsg : false;
                    const isNextNarrator = nextMsg ? (nextMsg.role === "narrator") && isNarratorMsg : false;

                    return (
                      <div key={msg.id}>
                        <MessageCardView
                          viewMode={viewMode}
                          isPreviousNarrator={isPrevNarrator}
                          isNextNarrator={isNextNarrator}
                          msg={msg}
                          entityLookup={SAMPLE_ENTITIES as any}
                          adventureState={SAMPLE_ADVENTURE_STATE}
                          onSpeak={noop}
                          onSpeakMessage={noop}
                          onDownload={noop}
                          onPause={noop}
                          onResume={noop}
                          onStop={noop}
                          speakingMsgId={null}
                          speakingVoiceKey={null}
                          isPaused={false}
                          defaultVoiceId={null}
                          onLocationClick={noop}
                          onSpeakerClick={noop}
                          isHidden={false}
                          onToggleHide={noop}
                          onReclassify={noop}
                          onDelete={noop}
                          onEdit={noop}
                          onRetry={canRetryLast ? noop : undefined}
                          canEdit={true}
                          isSceneIntro={msg.id === firstNarratorMsgId}
                          isLastMessage={msg.id === filteredMessages[filteredMessages.length - 1]?.id}
                          isAdmin={true}
                          isWorldOwner={true}
                          worldId="test-world"
                          currentUserId="test-user"
                        />
                      </div>
                    );
                  }}
                />

              </div>
            </MessageViewModeProvider>
          </ScrollArea>

          {/* ── Quick actions ── */}
          <div className="px-2 sm:px-4 pt-5 sm:pt-7 pb-2 sm:pb-3">
            <div className="max-w-3xl mx-auto">
              <QuickActionBubble
                isMobile={false}
                canAct={false}
                isGenerating={false}
                choices={[]}
                actionTemplates={[]}
                onSelectChoice={noop}
                onSelectAction={noop}
                onSendCommand={noop}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── V2 HUD trigger (visual consistency with Play page) ── */}
      <V2HUDTrigger
        onClick={() => setHudPanelOpen(true)}
        turn={1}
        inCombat={false}
        isMobile={false}
      />
      <V2HUDPanel open={hudPanelOpen} onOpenChange={setHudPanelOpen} hudProps={{} as any} />
    </div>
  );
}
